// ─── Delivery status ranking ─────────────────────────────────────────────────

export const DELIVERY_STATUS_RANK: Record<string, number> = {
  failed: 0,
  pending: 1,
  sent: 2,
  delivered: 3,
  read: 4,
};

/**
 * Returns whichever delivery status is "higher" (further along in the delivery lifecycle).
 * Prevents downgrading a message from 'read' → 'sent' on out-of-order Realtime events.
 */
export function bestDeliveryStatus(current: string, incoming: string): string {
  return (DELIVERY_STATUS_RANK[incoming] ?? 0) > (DELIVERY_STATUS_RANK[current] ?? 0)
    ? incoming
    : current;
}

// ─── Conversation filter matching ─────────────────────────────────────────────

/**
 * Returns true if a conversation with the given status belongs in the
 * `conversations` state for the given inbox filter. Usado por el Realtime
 * UPDATE handler para decidir si remover una conv del state cuando cambia.
 *
 * Desde commit efaed0b: 'archived' es un filtro INDEPENDIENTE — las convs
 * archivadas NO aparecen en active/pending/snoozed/resolved. El overlay de
 * archived ahora consume su propio stream del filtro 'archived', no comparte
 * el array con la vista normal.
 */
export function convMatchesFilter(convStatus: string, filter: string): boolean {
  // Archived SIEMPRE vive en el state `conversations` (junto a la vista
  // normal del filter). El overlay drawer de Archivados es una vista
  // derivada del mismo array — `archivedConversations = conversations
  // .filter(c => c.status === 'archived')`. Esto garantiza single source
  // of truth: las mutations (mark-as-read, pin, mute, archive, etc.) y los
  // realtime UPDATEs actualizan ambas vistas automáticamente.
  //
  // Sin esta unión, los archived chats desaparecen del state apenas llega
  // un UPDATE realtime (porque el handler hace prev.filter cuando el
  // status no matchea) → el overlay queda vacío después del primer tick.
  if (filter === 'archived') return convStatus === 'archived';
  if (convStatus === 'archived') return true;
  switch (filter) {
    case 'active':   return convStatus === 'pending' || convStatus === 'open';
    case 'pending':  return convStatus === 'pending';
    case 'snoozed':  return convStatus === 'snoozed';
    case 'resolved': return convStatus === 'resolved';
    default:         return true;
  }
}

// ─── Message renderability ───────────────────────────────────────────────────

/**
 * True si el mensaje aporta contenido visible al hilo. WhatsApp Web NUNCA
 * pinta un bubble vacío — y la reacción es un atributo del mensaje target
 * (campo `wa_messages.reactions`), NUNCA un mensaje propio.
 *
 * Casos que descarta (mensaje "fantasma"):
 *   - msg_type='reaction' (algunos formatos Evolution lo cuelan como msg).
 *   - Sin body, media, thumbnail, ni order, ni deleted_at, en un text/null.
 *
 * Casos que preserva:
 *   - deleted_at presente → render "🚫 mensaje eliminado".
 *   - Tipos media (image/video/audio/voice/sticker/document/location/contacts)
 *     aunque media_url/body aún no esté: el bg-download lo poblará vía
 *     realtime UPDATE.
 */
export function isRenderableMessage(msg: {
  body: string | null;
  media_url: string | null;
  media_thumbnail?: string | null;
  msg_type: string;
  order_info?: unknown;
  deleted_at?: string | null;
}): boolean {
  if (msg.msg_type === 'reaction') return false;
  if (msg.deleted_at) return true;
  if (msg.body && msg.body.trim().length > 0) return true;
  if (msg.media_url || msg.media_thumbnail) return true;
  if (msg.order_info) return true;
  const NON_TEXT_RENDERABLE = new Set([
    'image', 'video', 'audio', 'voice', 'sticker',
    'document', 'location', 'contacts', 'order',
  ]);
  return NON_TEXT_RENDERABLE.has(msg.msg_type);
}

// ─── Audio recording ──────────────────────────────────────────────────────────

export const AUDIO_MAX_BYTES = 50 * 1024 * 1024; // 50 MB
export const AUDIO_MIN_BYTES = 100;

/**
 * Returns a user-facing error message if the audio blob size is out of range,
 * or null if the size is acceptable.
 */
export function validateAudioBlob(sizeBytes: number): string | null {
  if (sizeBytes > AUDIO_MAX_BYTES) return 'Audio demasiado largo. Máximo permitido: 50MB.';
  if (sizeBytes < AUDIO_MIN_BYTES) return 'Audio vacío. Intenta grabar de nuevo.';
  return null;
}
